/****************************************************
 * Module Name:  CustomerFTPRenameColumnWrapper.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from 'react';
import { Tab, Tabs, Box, TextField, AppBar } from '@mui/material';
import ShapeProxy from '../../utility/proxy/manager/shapeProxy';
import ColorProxy from '../../utility/proxy/manager/colorProxy';
import ClarityProxy from '../../utility/proxy/manager/clarityProxy';
import CutProxy from '../../utility/proxy/manager/cutProxy';
import PolProxy from '../../utility/proxy/manager/polProxy';
import SymProxy from '../../utility/proxy/manager/symProxy';
import FloProxy from '../../utility/proxy/manager/floProxy';
import LabProxy from '../../utility/proxy/manager/labProxy';
import LocationProxy from '../../utility/proxy/manager/locationProxy';
import StoneTypeProxy from '../../utility/proxy/manager/stoneTypeProxy';
import Constants from '../../utility/constants';
import MilkyProxy from '../../utility/proxy/manager/milkyProxy';
import TingeProxy from '../../utility/proxy/manager/tingeProxy';

const proxyMap = {
    shape: ShapeProxy.GetAllEnabledShapeMini,
    color: ColorProxy.GetAllColorMini,
    clarity: ClarityProxy.GetAllClarityMini,
    cut: CutProxy.GetAllCutMini,
    pol: PolProxy.GetAllPolMini,
    sym: SymProxy.GetAllSymMini,
    flo: FloProxy.GetAllFloMini,
    lab: LabProxy.GetAllLabMini,
    location: LocationProxy.GetAllLocation,
    stonetype: StoneTypeProxy.GetAllStoneType,
    milky: MilkyProxy.GetAllMilkyMini,
    tinge: TingeProxy.GetAllTingeMini,
};

const tabData = [
    { title: 'Status', key: 'status' },
    { title: 'Shape', key: 'shape' },
    { title: 'Color', key: 'color' },
    { title: 'Clarity', key: 'clarity' },
    { title: 'Cut', key: 'cut' },
    { title: 'Pol', key: 'pol' },
    { title: 'Sym', key: 'sym' },
    { title: 'Flo', key: 'flo' },
    { title: 'Lab', key: 'lab' },
    { title: 'Location', key: 'location' },
    { title: 'Stone Type', key: 'stonetype' },
    { title: 'Milky', key: 'milky' },
    { title: 'Tinge', key: 'tinge' },
];

const CustomerFTPRenameColumnWrapper = ({
    renameColumnObj,
    renameColumnData
}) => {
    const [activeTab, setActiveTab] = React.useState(0);
    const [options, setOptions] = useState([]);
    const [optionsData, setOptionsData] = useState({});
    const [search, setSearch] = useState('');
    const [renamedOptions, setRenamedOptions] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [data, SetData] = useState(renameColumnObj);

    useEffect(() => {
        //this function will fetch all the data from the proxy and set the options data
        const newOptionsData = {};
        Object.entries(proxyMap).forEach(([key, proxy]) => {
            proxy((response) => {
                if (response && Array.isArray(response)) {
                    newOptionsData[key] = response.map((item) => item.name);
                } else {
                    newOptionsData[key] = [];
                }

                setOptionsData((prevData) => ({
                    ...prevData,
                    ...newOptionsData,
                }));
            });
        });
    }, []);

    useEffect(() => {
        Object.entries(data).forEach(([key, value]) => {
            //if there is no value, then we will rename the column to the original name
            if (!value) {
                let renamedData = "";
                if (optionsData[key] !== undefined) {
                    optionsData[key].map((originalName) => {
                        renamedData += originalName + ":" + originalName + ",";
                        return true;
                    });
                    data[key] = renamedData.substring(0, renamedData.length - 1)
                }
            }
        });
        renameColumnData(data);
    }, [data, optionsData]);

    useEffect(() => {
        const fetchData = () => {
            const activeTabKey = tabData[activeTab].key;

            if (optionsData[activeTabKey]) {
                const optionNames = optionsData[activeTabKey];
                setOptions(optionNames);
                setRenamedOptionsArray(activeTabKey, optionNames);
            }
            else if (activeTabKey === 'status') {
                const optionsArray = Constants.STONE_STATUS.filter(status => status.label !== "-- ANY --").map((status) => status.value);
                setOptionsData((prevData) => ({
                    ...prevData,
                    [activeTabKey]: optionsArray,
                }));
                setOptions(optionsArray);
                setRenamedOptionsArray(activeTabKey, optionsArray);
            }
        };
        fetchData();
    }, [activeTab]);

    //this function will first check if any column values already have a renamed value, if so, we will keep the value else we will rename the column to the original name
    const setRenamedOptionsArray = (activeTabKey, optionNames) => {
        const renamedMap = {};
        if (renameColumnObj[activeTabKey]) {
            const renameValue = renameColumnObj[activeTabKey];
            const renameItems = renameValue !== "" && renameValue !== null && renameValue.includes(',')
                ? renameValue.split(',')
                : [renameValue];

            renameItems.forEach((item) => {
                const [key, value = ""] = item.split(':').map((part) => part.trim());
                if (key) renamedMap[key] = value;
            });

            const renamedArray = optionNames.map((option) => {
                const value = renamedMap[option];
                if (value === undefined) {
                    return option;
                } else {
                    return value;
                }
            });
            setRenamedOptions(renamedArray);
        }
        else {
            setRenamedOptions([...optionNames]);
        }
    }

    useEffect(() => {
        let renamedData = "";
        options.map((originalName, index) => {
            renamedData += originalName + ":" + (renamedOptions[index] == null ? originalName : renamedOptions[index].trim()) + ",";
            return true;
        });
        const activeTabKey = tabData[activeTab].key;
        SetData({
            ...data,
            [activeTabKey]: renamedData.substring(0, renamedData.length - 1)
        });
    }, [renamedOptions]);


    const updateSelected = (item) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(item)
                ? prevSelected.filter((i) => i !== item)
                : [...prevSelected, item]
        );
    };

    const filteredOptions = options.filter((option) =>
        option.toLowerCase().includes(search.toLowerCase())
    );


    return (
        <Box>
            {/* Tabs */}
            <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 50 }}>
                <Tabs
                    value={activeTab}
                    onChange={(event, newValue) => setActiveTab(newValue)}
                    indicatorColor="secondary"
                    textColor="inherit"
                    variant="scrollable"
                    aria-label="full width tabs example"
                    sx={{
                        "& .MuiTab-root": {
                            color: "#fafafa",
                            fontWeight: "bold",
                            textAlign: 'center',
                            paddingBottom: 2
                        },
                        "& .Mui-selected": {
                            bgcolor: "#0277bd !important",
                            color: "#fafafa !important",
                            fontWeight: "bold !important",
                            paddingBottom: 2
                        }
                    }}
                    TabIndicatorProps={{ style: { background: "#0277bd" } }}
                >
                    {tabData.map((tab, index) => (
                        <Tab
                            key={index}
                            label={tab.title}
                        />
                    ))}
                </Tabs>
            </AppBar>

            {/* Content */}
            <Box style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', width: '50%' }}>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search"
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginBottom: '10px', marginTop: '20px' }}
                    />
                    <div
                        style={{
                            overflowY: 'auto',
                            width: '100%',
                            maxHeight: '250px',
                            minHeight: '250px',
                            border: '1pt solid black',
                            justifyContent: 'center',
                        }}
                    >
                        <table style={{ width: '100%' }}>
                            <tbody>
                                {filteredOptions.map((item, index) => (
                                    <tr key={index}>
                                        <td
                                            onClick={() => updateSelected(item)}
                                            style={{
                                                backgroundColor: selectedItems.includes(item) ? 'grey' : 'white',
                                                color: selectedItems.includes(item) ? 'white' : 'black',
                                                paddingLeft: 10,
                                                paddingRight: 10,
                                            }}
                                        >
                                            {item}
                                        </td>
                                        <td>
                                            <TextField
                                                margin="normal"
                                                fullWidth
                                                size="small"
                                                label={renamedOptions[index]}
                                                placeholder={`Rename column ${item} to`}
                                                value={renamedOptions[index]}
                                                onChange={(e) => {
                                                    const updatedRenamedOptions = [...renamedOptions];
                                                    updatedRenamedOptions[index] = e.target.value;
                                                    setRenamedOptions(updatedRenamedOptions);
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Box>
        </Box>
    );
};


export default CustomerFTPRenameColumnWrapper;